/**
 * BLU-1400: a client's copy of its 3PL's garment (a supply alias), read-only.
 * The 3PL keeps the garment's details, so this is facts as text and one Close
 * button: no form, no Save, no Default cost. The client's own price from the
 * 3PL shows only when the list read carried it (`supplyOwner.pricesShown`:
 * own catalog, a role that may see costs).
 *
 * Reads nothing: the row is the `inventory.listProductsGrouped` row.
 */
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import ProductImageWithHover from '@/features/purchase-orders/components/ProductImageWithHover';
import { formatSupplyPrice, type SupplyAliasRow } from '../../utils/supplyAliasGroups';

export interface SupplyAliasDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: SupplyAliasRow | null;
}

export function SupplyAliasDetailDialog({ open, onOpenChange, row }: SupplyAliasDetailDialogProps) {
  const { t } = useTranslation('inventory');
  if (!row) return null;

  const owner = row.supplyOwner.name;
  const notSet = t('supplyAliasDialog.notSet');
  const weight = row.weightValue != null ? `${row.weightValue} ${row.weightUnit ?? ''}`.trim() : notSet;
  // The owner's name is part of the sentence, so a released mapping (no
  // owner) shows no price line at all.
  const showPrice = row.supplyOwner.pricesShown && owner !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('supplyAliasDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex min-w-0 items-start gap-3">
          <ProductImageWithHover imageUrl={row.imageUrl ?? undefined} productSku={row.sku} size="large" />
          <dl className="grid min-w-0 flex-1 grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-muted-foreground">{t('editSkuModal.skuLabel')}</dt>
            <dd className="break-all font-mono">{row.sku}</dd>
            <dt className="text-muted-foreground">{t('editSkuModal.titleLabel')}</dt>
            <dd className="[overflow-wrap:anywhere]">{row.title || notSet}</dd>
            <dt className="text-muted-foreground">{t('editSkuModal.sizeLabel')}</dt>
            <dd>{row.size || notSet}</dd>
            <dt className="text-muted-foreground">{t('editSkuModal.colorLabel')}</dt>
            <dd className="[overflow-wrap:anywhere]">{row.color || notSet}</dd>
            <dt className="text-muted-foreground">{t('editSkuModal.weightLabel')}</dt>
            <dd className="tabular-nums">{weight}</dd>
            {showPrice && (
              <>
                <dt className="text-muted-foreground">{t('products.supplyAlias.priceFrom', { owner })}</dt>
                <dd className="tabular-nums">
                  {row.supplyPrice != null
                    ? formatSupplyPrice(row.supplyPrice, row.supplyOwner.currency)
                    : t('products.supplyAlias.priceNotSet')}
                </dd>
              </>
            )}
          </dl>
        </div>

        {/* The dialog's description: Radix points aria-describedby at it. */}
        <DialogDescription asChild>
          <div>
            <NoteLine as="p">
              {owner !== null ? t('supplyAliasDialog.note', { owner }) : t('supplyAliasDialog.noteNoOwner')}
            </NoteLine>
          </div>
        </DialogDescription>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
